import { useCallback, useMemo, useRef, useState } from "react";

import {
  type Attempt,
  type SessionResult,
  SessionRecorder,
  recordAttempt,
} from "@nav-games/games-core";
import { estimatedPosition, type EpInput, type EpResult } from "@nav-games/chartwork";
import { formatHeading, normalizeDeg } from "@nav-games/compass";

import { HelpButton, HelpPanel } from "../../ui/HelpPanel";
import { EpFigure } from "../../ui/EpFigure";

interface EpProps {
  onNavigate: (to: string) => void;
}

const MODULE = "chartwork";
const GAME = "ep";
const CARDS_PER_SESSION = 8;
const CHOICES_PER_CARD = 4;

interface Card {
  input: EpInput;
  result: EpResult;
  target: number;
  choices: readonly string[];
}

function randInt(lo: number, hi: number): number {
  return lo + Math.floor(Math.random() * (hi - lo + 1));
}

function buildCard(withLeeway: boolean): Card {
  for (let attempt = 0; attempt < 60; attempt++) {
    const courseSteeredDeg = randInt(0, 359);
    const boatSpeedKts = randInt(4, 6);
    const hours = Math.random() < 0.5 ? 1 : 2;
    const setDeg = normalizeDeg(courseSteeredDeg + randInt(40, 320));
    const rateKts = randInt(1, 3);
    const common = { courseSteeredDeg, boatSpeedKts, hours, setDeg, rateKts };
    let input: EpInput = common;
    if (withLeeway) {
      const leewayDeg = randInt(4, 8);
      // Wind clearly on one bow so the leeway side is unambiguous.
      const windSide = Math.random() < 0.5 ? 1 : -1;
      const windFromDeg = normalizeDeg(courseSteeredDeg + windSide * randInt(50, 130));
      input = { ...common, leewayDeg, windFromDeg };
    }
    const result = estimatedPosition(input);

    const offset = Math.abs(
      ((result.courseMadeGoodDeg - courseSteeredDeg + 540) % 360) - 180,
    );
    if (offset < 6 || result.distanceMadeGoodNm < 0.5) continue;

    const target = normalizeDeg(Math.round(result.courseMadeGoodDeg));
    const pool = [
      normalizeDeg(Math.round(courseSteeredDeg)), // confused with the heading
      normalizeDeg(Math.round(setDeg)), // confused with the tide
      normalizeDeg(Math.round(2 * courseSteeredDeg - result.courseMadeGoodDeg)), // set the wrong way
      normalizeDeg(Math.round(result.waterTrackDeg)), // stopped at the water track
    ];
    const chosen: number[] = [];
    const seen = new Set<number>([target]);
    for (const cand of pool) {
      if (chosen.length >= CHOICES_PER_CARD - 1) break;
      if (!seen.has(cand)) {
        seen.add(cand);
        chosen.push(cand);
      }
    }
    let off = 7;
    while (chosen.length < CHOICES_PER_CARD - 1) {
      for (const cand of [normalizeDeg(target + off), normalizeDeg(target - off)]) {
        if (chosen.length >= CHOICES_PER_CARD - 1) break;
        if (!seen.has(cand)) {
          seen.add(cand);
          chosen.push(cand);
        }
      }
      off += 5;
    }
    const all = [target, ...chosen].map((n) => `${formatHeading(n)}°`);
    for (let i = all.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      const ai = all[i]!;
      all[i] = all[j]!;
      all[j] = ai;
    }
    return { input, result, target, choices: all };
  }
  // Fallback.
  const input: EpInput = {
    courseSteeredDeg: 0,
    boatSpeedKts: 5,
    hours: 2,
    setDeg: 90,
    rateKts: 1.5,
  };
  const result = estimatedPosition(input);
  const target = normalizeDeg(Math.round(result.courseMadeGoodDeg));
  return {
    input,
    result,
    target,
    choices: [`${formatHeading(target)}°`, "000°", "090°", "350°"],
  };
}

export function Ep({ onNavigate }: EpProps) {
  const [withLeeway, setWithLeeway] = useState<boolean | null>(null);

  if (withLeeway === null) {
    return <Picker onStart={setWithLeeway} onBack={() => onNavigate("/play/chartwork")} />;
  }
  return <Game withLeeway={withLeeway} onNavigate={onNavigate} />;
}

function Picker({
  onStart,
  onBack,
}: {
  onStart: (withLeeway: boolean) => void;
  onBack: () => void;
}) {
  return (
    <>
      <h2>Estimated position</h2>
      <p className="muted">
        From a fix, plot the water track then the tidal stream to find the EP
        and read off the course made good.
      </p>
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Choose a level</h3>
        <div
          className="row"
          style={{ flexDirection: "column", gap: 8, alignItems: "stretch" }}
        >
          <button onClick={() => onStart(false)}>Simple — DR + tide</button>
          <button className="secondary" onClick={() => onStart(true)}>
            Advanced — allow for leeway (wind)
          </button>
        </div>
        <p className="small muted" style={{ margin: "10px 0 0" }}>
          Advanced adds a wind and a leeway angle: the boat crabs to leeward of
          her heading, so the water track is offset before the tide is applied.
        </p>
      </div>
      <button className="secondary" onClick={onBack}>
        ← Back to Chartwork
      </button>
    </>
  );
}

function Game({
  withLeeway,
  onNavigate,
}: {
  withLeeway: boolean;
  onNavigate: (to: string) => void;
}) {
  const recorder = useMemo(
    () => new SessionRecorder({ module: MODULE, game: GAME, length: CARDS_PER_SESSION }),
    [],
  );

  const [index, setIndex] = useState(0);
  const [card, setCard] = useState<Card>(() => buildCard(withLeeway));
  const [shownAt, setShownAt] = useState<number>(() => Date.now());
  const [chosen, setChosen] = useState<string | null>(null);
  const [result, setResult] = useState<SessionResult | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const sessionStartedAt = useRef<number>(Date.now());

  const advance = useCallback(() => {
    if (index + 1 >= CARDS_PER_SESSION) {
      setResult(recorder.summarise());
      return;
    }
    setIndex((i) => i + 1);
    setCard(buildCard(withLeeway));
    setShownAt(Date.now());
    setChosen(null);
  }, [index, recorder, withLeeway]);

  const onChoose = useCallback(
    (choice: string) => {
      if (chosen || result) return;
      const ok = choice === `${formatHeading(card.target)}°`;
      const attempt: Omit<Attempt, "module"> = {
        char: withLeeway ? "ep-leeway" : "ep",
        ok,
        ms: Date.now() - shownAt,
        ts: Date.now(),
      };
      recorder.log(attempt);
      recordAttempt({ ...attempt, module: MODULE });
      setChosen(choice);
    },
    [card, chosen, recorder, result, shownAt, withLeeway],
  );

  if (result) {
    return (
      <EndCard
        result={result}
        sessionStartMs={sessionStartedAt.current}
        onAgain={() => {
          setIndex(0);
          setCard(buildCard(withLeeway));
          setShownAt(Date.now());
          setChosen(null);
          setResult(null);
          sessionStartedAt.current = Date.now();
        }}
        onBack={() => onNavigate("/play/chartwork")}
      />
    );
  }

  const revealed = chosen !== null;
  const correctStr = `${formatHeading(card.target)}°`;
  const i = card.input;

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <HelpButton onOpen={() => setHelpOpen(true)} />
      </div>
      <HelpPanel section={helpOpen ? "chartwork" : null} onClose={() => setHelpOpen(false)} />
      <p className="small muted">
        Question {index + 1} of {CARDS_PER_SESSION}
        {withLeeway && " · with leeway"}
      </p>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Estimated position</h3>
        <p style={{ marginBottom: 12 }}>
          You steer <strong>{formatHeading(i.courseSteeredDeg)}°T</strong> at{" "}
          <strong>{i.boatSpeedKts} kt</strong> for <strong>{i.hours} h</strong>.
          The tide sets <strong>{formatHeading(i.setDeg)}°T</strong> at{" "}
          <strong>{i.rateKts} kt</strong>.
          {withLeeway && i.windFromDeg !== undefined && i.leewayDeg !== undefined && (
            <>
              {" "}
              The wind is from <strong>{formatHeading(i.windFromDeg)}°T</strong>;
              allow <strong>{i.leewayDeg}°</strong> leeway.
            </>
          )}
          <br />
          What is the <strong>course made good</strong> (°T)?
        </p>

        <div style={{ display: "flex", justifyContent: "center" }}>
          <EpFigure input={card.input} result={card.result} revealed={revealed} size={300} />
        </div>
        <p className="small muted" style={{ textAlign: "center", margin: "4px 0 12px" }}>
          <span style={{ color: "#d97706" }}>▸ water track</span> ·{" "}
          <span style={{ color: "#1e6091" }}>▸▸▸ tidal stream</span>
          {revealed && (
            <>
              {" "}
              · <span style={{ color: "#b8307a" }}>▸▸ course made good</span>
            </>
          )}
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {card.choices.map((c) => {
            const isCorrect = c === correctStr;
            const isChosen = c === chosen;
            return (
              <button
                key={c}
                className="secondary"
                disabled={revealed}
                onClick={() => onChoose(c)}
                style={{
                  fontSize: 20,
                  padding: "14px 0",
                  fontWeight: 600,
                  background:
                    revealed && isCorrect
                      ? "rgba(44, 122, 59, 0.18)"
                      : revealed && isChosen
                        ? "rgba(184, 48, 42, 0.18)"
                        : undefined,
                }}
              >
                {c}
              </button>
            );
          })}
        </div>

        {revealed && (
          <div
            role="status"
            aria-live="polite"
            style={{
              padding: 12,
              borderRadius: 6,
              background:
                chosen === correctStr
                  ? "rgba(44, 122, 59, 0.10)"
                  : "rgba(184, 48, 42, 0.10)",
            }}
          >
            <p style={{ margin: 0, fontWeight: 600 }}>
              {chosen === correctStr
                ? `✓ Correct — course made good ${correctStr}T.`
                : `✗ Not quite — course made good ${correctStr}T.`}
            </p>
            {withLeeway && i.leewayDeg !== undefined && (
              <p className="small muted" style={{ margin: "8px 0 0" }}>
                Heading {formatHeading(i.courseSteeredDeg)}°T → water track{" "}
                <strong>{formatHeading(card.result.waterTrackDeg)}°T</strong>{" "}
                after {i.leewayDeg}° leeway, then apply the tide.
              </p>
            )}
            <p className="small muted" style={{ margin: "8px 0 0" }}>
              Water track {card.result.distanceRunNm.toFixed(1)} nm + tide{" "}
              {card.result.tideDriftNm.toFixed(1)} nm gives an EP{" "}
              {card.result.distanceMadeGoodNm.toFixed(1)} nm from the start —
              speed over the ground{" "}
              <strong>{card.result.speedOverGroundKts.toFixed(1)} kt</strong>.
            </p>
            <button onClick={advance} style={{ marginTop: 12, width: "100%" }}>
              {index + 1 >= CARDS_PER_SESSION ? "See score →" : "Next question →"}
            </button>
          </div>
        )}
      </div>

      <button className="secondary" onClick={() => onNavigate("/play/chartwork")}>
        ← Back to Chartwork
      </button>
    </>
  );
}

function EndCard({
  result,
  sessionStartMs,
  onAgain,
  onBack,
}: {
  result: SessionResult;
  sessionStartMs: number;
  onAgain: () => void;
  onBack: () => void;
}) {
  const elapsedS = Math.round((Date.now() - sessionStartMs) / 1000);
  return (
    <>
      <h2>Session complete</h2>
      <div className="card">
        <p>
          <strong>{result.correct}</strong> / {result.total} correct
          {result.total > 0 && (
            <> ({Math.round((result.correct / result.total) * 100)}%)</>
          )}{" "}
          in {elapsedS}s.
        </p>
      </div>
      <div
        className="row"
        style={{ gap: 8, flexDirection: "column", alignItems: "stretch" }}
      >
        <button onClick={onAgain}>Play another</button>
        <button className="secondary" onClick={onBack}>
          ← Back to Chartwork
        </button>
      </div>
    </>
  );
}
